import { pwaState, registerServiceWorker } from "./pwa";
import type { RigReport } from "./render/rig";
import type { RemoteBodyView, Renderer } from "./render/renderer";
import { assetStats, texture as assetTexture } from "./render/assets";
import { cardsWanted, Menu, menuWanted, type MenuView } from "./menu";
import { bootWanted, loadingFor, loadingView, nextPaint, readLoading, showLoading, watchBoot, type LoadingView } from "./loading";
import { walletAct, walletState } from "./wallet";
import { COUNTER_URL, HOSTS } from "./config";
import { clampSettings, saveSettings, type Settings } from "./settings";
import { crawlSeen, crawlWanted, OpeningCrawl, type CrawlView } from "./crawl";
import { parseTag } from "@shared/identity/identity";
import type { counterView } from "@shared/economy/counter";
import { Game, type NetConfig } from "./game";
import type { NetClient } from "./net/netclient";
import type { BotStep } from "./bot";
import { SIM_HZ } from "@shared/sim/constants";
import type { SimEvent } from "@shared/sim/world";
import type { FileView } from "./file";
import type { SocialMsg } from "@shared/net/protocol";
import { modsFor, weaponDefOf } from "@shared/sim/player";
import type { ScreenStats } from "./render/screens";

/** Headless/state hook used by probes and CI. Everything here is read-only or deterministic. */
export interface GameHook {
  ready: true;
  simHz: number;
  game: Game;
  advance: (ticks: number) => void;
  setRealtime: (on: boolean) => void;
  setBot: (plan: BotStep[] | null) => void;
  botStatus: () => Game["botStatus"];
  state: () => {
    tick: number;
    pos: { x: number; y: number; z: number };
    vel: { x: number; y: number; z: number };
    yaw: number;
    pitch: number;
    stance: string;
    grounded: boolean;
    health: number;
    ammo: number;
    slot: number;
    stats: typeof Game.prototype.player.stats;
    dummies: { id: number; alive: boolean; health: number; pos: { x: number; y: number; z: number } }[];
    loop: typeof Game.prototype.stats;
    audio: Record<string, number>;
    /** the last footstep heard from another file (Stage 80) */
    heard: { n: number; pan: number; gain: number; distance: number };
    /** and the last shot (Stage 81) */
    heardShot: { n: number; pan: number; gain: number; delay: number; distance: number };
    heardSnap: { n: number; pan: number; distance: number };
    /** what closed the file last (Stage 83) */
    closedBy: { name: string; x: number; z: number; known: boolean };
    render: { post: boolean; district: string; frames: number; internalScale: number; levelCalls: number; calls: number; triangles: number; geometries: number; textures: number; programs: number; tracers: number };
    level: string;
    wake: { phase: string; timeLeft: number; score: number[]; pulses: number; nodes: { id: number; label: string; owner: number; hold: number; contested: boolean; puller: number; boost: number; flips: number }[] } | null;
    team: number;
    hash: string;
    /** Build sheet the sim is running for the local player (loadout applied). */
    mods: Record<string, number>;
    maxShield: number;
    maxHealth: number;
    /** the weapon definition the sim runs for the held slot (firmware applied) */
    weaponDef: { id: string; rpm: number; magSize: number; damage: number; burst: { count: number; rpm: number } | null };
    /** City life (render-only): crowd size and a sample of positions, the tram's coordinate along its line, PA lines spoken. */
    /** identity & rituals (Stage 8): the file's identity, HUD ritual state, social messages received, the Debt target */
    identity: FileView["identity"];
    rituals: { receipt: { open: boolean; lines: string[]; printed: number; stamped: boolean; signed: number }; dossierOpen: boolean; dossierEntries: number; debtText: string; riteOpen: boolean; riteTitle: string; raised: { dossier: number; debt: number; rite: number; card: number } };
    social: SocialMsg[];
    debtTargetId: number;
    /** the Deadletter Office: renovation pieces built, trophies on the wall, the range ghost */
    hub: { renovations: number; trophies: number; ghost: { recording: boolean; playing: boolean; best: number | null; runs: number; last: number; pose: { x: number; z: number } | null }; fileLoaded: boolean } | null;
    life: { clock: number; crowd: number; sample: { x: number; z: number }[]; tram: number | null; tramNear: boolean; tramDist: number; pa: string[]; steam: boolean; ads: number; adRedraws: number; adFed: number; adCarry: number; adFrames: number[]; ship: { x: number; y: number; z: number }; blinkers: number; flicker: boolean };
  };
  /** Ghostfile view: account, Depth/XP/Scrip, loadout legality, ledger. */
  file: () => FileView;
  /** Replace the raw loadout the client will send at the next link (offline: applies now). */
  setLoadout: (raw: Record<string, unknown>) => void;
  toggleFile: (on?: boolean) => void;
  toggleGraph: (on?: boolean) => void;
  /** Buy (or refund) a Ledger Graph node through the ledger shop of the linked host. */
  buy: (nodeId: string, refund?: boolean) => Promise<{ ok: boolean; reason?: string }>;
  /** Sign the post-match receipt (Enter). */
  sign: () => boolean;
  /** Endgame (Stage 11): the board as the file sees it; claim / rewrite / cosmetics; the Audit room; presets. */
  endgame: () => Game["file"]["endgame"] & { wakelight: number; rewrites: number; theme: string | null; cosmetics: string[]; presets: { name: string }[]; aliases: string[]; audits: { week: number; best: number; played: number } | null; mode: string; gravity: number };
  loadEndgame: () => Promise<boolean>;
  claim: (id: string) => Promise<{ ok: boolean; reason?: string }>;
  rewrite: () => Promise<{ ok: boolean; reason?: string }>;
  cosmetic: (body: Record<string, unknown>) => Promise<{ ok: boolean; reason?: string }>;
  loadPreset: (slot: number) => boolean;
  joinAudit: () => void;
  /** The counter-ledger (Stage 11b): the panel's view, the wallet link, a market buy, wear, the name, reconcile; the skins others wear as the snapshot carries them. */
  counter: () => { view: ReturnType<typeof counterView> | null; wallet: string | null; last: string; info: unknown; tint: string | null; skinMap: boolean; skinBound: boolean; assets: { requested: number; loaded: number; failed: number }; remotes: { id: number; name: string; tag: string; skin: number }[] };
  link: () => Promise<{ ok: boolean; reason?: string }>;
  buySkin: (listing: number) => Promise<{ ok: boolean; reason?: string }>;
  /** the sinks (Stage 19): the Deep Wake pass and private room-hours, both 100% burned */
  buySeason: () => Promise<{ ok: boolean; reason?: string }>;
  buyRoomHours: (hours: number) => Promise<{ ok: boolean; reason?: string }>;
  /** private rooms (Stage 20): open one against a room-hour, or look one up by its invite code */
  openRoom: (hours?: number, rules?: Record<string, unknown>) => Promise<{ ok: boolean; reason?: string; code?: string; room?: string; join?: string }>;
  lookupRoom: (code: string) => Promise<{ ok: boolean; reason?: string; url?: string; players?: number }>;
  wearSkin: (token: number) => Promise<{ ok: boolean; reason?: string }>;
  registerName: (name: string) => Promise<{ ok: boolean; reason?: string }>;
  reconcile: () => Promise<{ ok: boolean; reason?: string }>;
  /** The opening crawl (Stage 12): its live state, a skip, and the title's click. */
  /** Load a declared asset through the pipeline and say whether it decoded (Stage 44). */
  loadAsset: (id: string) => Promise<boolean>;
  /** installability: is the service worker registered and controlling this page (Stage 45) */
  /** the frame monitor (Stage 50): null unless the page was opened with ?perf=1 */
  perf: () => ReturnType<NonNullable<Game["perf"]>["state"]> | null;
  /** the camera (Stage 60): third or first person, where it is, the reticle, and the frame's draw calls */
  view: () => ReturnType<Game["renderer"]["view"]> & { calls: number; breakdown: Record<string, number>; eye: { x: number; y: number; z: number }; spawn: number };
  setView: (third: boolean) => void;
  /** the probe's ruler: the body off with the camera where it is */
  hideBody: (on: boolean) => void;
  /** the body's bones and pose (Stage 63): the local rig, or a remote's by id */
  rig: (id?: number) => RigReport;
  /** the third-person presentation: which muzzle light is lit and where the Kernel's filament hangs */
  presentation: () => ReturnType<Renderer["presentation"]>;
  /** hold the HUD's flash panels open so a probe can photograph one (Stage 72) */
  holdFlash: (on: boolean) => void;
  kickRemote: (id: number) => void;
  /** offline: put remotes on screen from the wire's fields, or take them away with null */
  injectRemote: (views: RemoteBodyView[] | null) => void;
  pwa: () => Promise<{ supported: boolean; registered: boolean; controlled: boolean; scope: string | null }>;
  crawl: () => CrawlView | null;
  /** Whether audio is actually live, and whether the crawl's hum is actually sounding (Stage 177). */
  audioLive: () => { ready: boolean; humming: boolean };
  /** The city's moving signs: how many hold a decoder, how many are advancing (Stage 633). */
  screens: () => ScreenStats;
  crawlSkip: () => boolean;
  crawlFinish: () => void;
  crawlPause: (on: boolean) => void;
  crawlSeek: (t: number) => void;
  /** The menu flow (Stage 13): its view, a key, a choice, the pause menu; settings; the audio cues fired. */
  menu: () => MenuView | null;
  /** The loading card (every trip that reloads the page): whether it is up, where to, and the stage it has reached; null when none is on the page. */
  loading: () => LoadingView | null;
  menuKey: (code: string) => void;
  menuChoose: (id: string) => string | null;
  pause: () => void;
  menuPause: (on: boolean) => void;
  settings: () => Settings & { applied: { sensitivity: number; fov: number; crt: { grain: number; scanline: number; vignette: number; aberration: number }; volumes: { master: number; sfx: number; bed: number }; buses: { master: number; sfx: number; bed: number } | null } };
  setSetting: (key: keyof Settings, value: number | boolean) => Settings;
  audioCues: () => Record<string, number>;
  /** where the draw calls go, by scene group: the budget check names what blew it */
  renderBreakdown: () => Record<string, number>;
  /** the probe's damage: drops the local file's health (the low-health pulse) */
  hurt: (dmg: number) => number;
  /** THE RUN (Stage 14): the view as the client has it; the payout to the wallet */
  run: () => Game["runView"];
  payout: () => Promise<{ ok: boolean; reason?: string }>;
  sellSkin: (token: number, price: number) => Promise<{ ok: boolean; reason?: string }>;
  /** the PrizeVault (Stage 15): posted prizes for the linked wallet, a sponsored claim */
  prizes: () => Promise<{ epoch: number; kind: string; period: number; amount: string; reason: string; claimed: boolean }[]>;
  claimPrize: (epoch: number) => Promise<{ ok: boolean; reason?: string }>;
  /** Campaign (Stage 10): state, dialogue advance/choose, contracts desk, launch, faction, protocols. */
  campaign: () => ReturnType<Game["campaign"]["view"]>;
  /** Stage 705: the WORLD MAP in the city (null elsewhere): open or shut it, pick a district, TRAVEL (under `?nonav=1` it only says where) */
  worldMap: (o?: { open?: boolean; select?: string; travel?: string }) => (ReturnType<NonNullable<Game["worldMap"]>["view"]> & { travelUrl: string | null }) | null;
  /** Stage 667: the fixers standing in the rendered scene, by name, where they stand and which way they face */
  figures: () => { id: string; x: number; z: number; yaw: number }[];
  /** Stage 32: is this the touch build, and where are the controls actually sitting? */
  mobile: () => { on: boolean; engaged: boolean; buttons: { id: string; x: number; y: number; w: number; h: number }[]; mirror: boolean; scale: number };
  dialogueAdvance: (choice?: number) => boolean;
  contracts: (on?: boolean) => void;
  launch: (id: string) => { ok: boolean; reason?: string };
  /** a crew (Stage 49): start one from a contract, or join one by code; under `?nonav=1` neither travels, and campaign().crewTarget says where it would */
  crewStart: (id: string) => { ok: boolean; reason?: string; code?: string; url?: string };
  crewJoin: (code: string) => Promise<{ ok: boolean; reason?: string; code?: string; url?: string }>;
  pickFaction: (f: "estate" | "clockeaters" | "cells") => Promise<boolean>;
  wear: (ids: string[]) => Promise<string[]>;
  playScript: (id: string) => void;
  /** Equip a moniker (worn online only if earned). */
  setMoniker: (id: string | null) => void;
  events: () => SimEvent[];
  clearEvents: () => void;
  resumeAudio: () => void;
  setDrawing: (on: boolean) => void;
  connect: (cfg: NetConfig) => void;
  reconnect: () => void;
  disconnect: () => void;
  net: () => {
    online: boolean;
    status: string;
    /** true once the first exact local state arrived (team, spawn, weapon state are authoritative). */
    synced: boolean;
    playerId: number;
    token: string;
    rttMs: number;
    joinMs: number;
    /** how many times the join was asked before the room let this client in (Stage 155) */
    joinAsks: number;
    pending: number;
    stats: NetClient["stats"];
    game: Game["netStats"];
    remotes: ReturnType<NetClient["remoteViews"]>;
    kickReason: string;
  } | null;
}

declare global {
  interface Window {
    __game: GameHook;
  }
}

/**
 * The loading card on boot: the descriptor the trip wrote (or one the URL implies, on a shared link)
 * on screen before the world is built, and gone once the game is playable — the world built, a first
 * frame drawn, the room joined when there is one. index.html's boot snippet has usually painted it
 * already, from the same descriptor, before this bundle arrived; the card adopts that element. The
 * build is synchronous, so the card gets one frame to be painted in first.
 */
const bootQ = new URLSearchParams(location.search);
const bootDesc = readLoading();
const bootCard = bootWanted(bootQ, !!bootDesc, menuWanted(bootQ)) ? showLoading(bootDesc ?? loadingFor(location.href), "city") : null;
if (bootCard) await nextPaint();
else document.getElementById("loading")?.remove(); // a snippet's card this boot does not want

const canvas = document.getElementById("view") as HTMLCanvasElement;
const hudRoot = document.getElementById("hud") as HTMLElement;
const game = new Game(canvas, hudRoot);

window.__game = {
  ready: true,
  simHz: SIM_HZ,
  game,
  advance: (n) => game.advance(n),
  setRealtime: (on) => {
    game.realtime = on;
  },
  setBot: (plan) => game.setBot(plan),
  botStatus: () => game.botStatus,
  state: () => ({
    tick: game.world.tick,
    pos: { ...game.player.pos },
    vel: { ...game.player.vel },
    yaw: game.player.yaw,
    pitch: game.player.pitch,
    stance: game.player.stance,
    grounded: game.player.grounded,
    health: game.player.health,
    ammo: game.player.weapon.ammo[game.player.weapon.slot] ?? 0,
    slot: game.player.weapon.slot,
    stats: { ...game.player.stats },
    dummies: game.world.dummies.map((d) => ({ id: d.id, alive: d.alive, health: d.health, pos: { ...d.pos } })),
    loop: { ...game.stats },
    audio: { ...game.audio.fired },
    heard: { ...game.heard },
    heardShot: { ...game.heardShot },
    heardSnap: { ...game.heardSnap },
    closedBy: { ...game.closedByLast },
    render: { post: !!game.renderer.post, district: game.renderer.district, frames: game.renderer.frames, internalScale: game.renderer.post.scale, levelCalls: game.renderer.levelCalls, calls: game.renderer.renderer.info.render.calls, triangles: game.renderer.renderer.info.render.triangles, geometries: game.renderer.renderer.info.memory.geometries, textures: game.renderer.renderer.info.memory.textures, programs: game.renderer.renderer.info.programs?.length ?? 0, tracers: game.renderer.liveVfx },
    level: game.levelId,
    wake: game.world.wake ? { phase: game.world.wake.phase, timeLeft: game.world.wake.timeLeft, score: [...game.world.wake.score], pulses: game.world.wake.pulses, nodes: game.world.wake.nodes.map((n) => ({ id: n.id, label: n.label, owner: n.owner, hold: n.hold, contested: n.contested, puller: n.puller, boost: n.boost, flips: n.flips })) } : null,
    team: game.player.team,
    hash: game.hash(),
    mods: { ...modsFor(game.player) },
    weaponDef: (() => { const d = weaponDefOf(game.player); return { id: d.id, rpm: d.rpm, magSize: d.magSize, damage: d.damage, burst: d.burst ?? null }; })(),
    maxShield: game.player.maxShield,
    maxHealth: game.player.maxHealth,
    identity: game.file.identityView(),
    rituals: (() => {
      const r = game.hud.receiptState;
      const q = (sel: string) => document.querySelector("#hud " + sel) as HTMLElement | null;
      return { receipt: { open: r.open, lines: r.lines.slice(), printed: r.printed, stamped: r.stamped, signed: r.signed }, dossierOpen: !(q(".dossier")?.hidden ?? true), dossierEntries: document.querySelectorAll("#hud .dossier .ent:not(.dim)").length, debtText: q(".debt")?.classList.contains("on") ? (q(".debt")?.textContent ?? "") : "", riteOpen: !(q(".rite")?.hidden ?? true), riteTitle: q(".rite .rt")?.textContent ?? "", raised: { ...game.hud.raised } };
    })(),
    social: game.socialLog.slice(),
    debtTargetId: game.debtTargetId,
    hub: game.renderer.hub ? { renovations: game.renderer.hub.renovations, trophies: game.renderer.hub.trophyCount, ghost: { recording: game.ghost?.recording ?? false, playing: game.ghost?.playing ?? false, best: game.ghost?.best?.seconds ?? null, runs: game.ghost?.runs ?? 0, last: game.ghost?.lastSeconds ?? 0, pose: (() => { const g = game.ghost?.pose() ?? null; return g ? { x: g.x, z: g.z } : null; })() }, fileLoaded: game.file.loaded } : null,
    life: (() => {
      const L = game.renderer.life;
      const eye = { x: game.player.pos.x, y: game.player.pos.y + 1.6, z: game.player.pos.z };
      return { clock: L.clock, crowd: L.crowd?.count ?? 0, sample: L.crowd?.sample(8) ?? [], tram: L.tram ? L.tram.position : null, tramNear: !!L.tram?.near, tramDist: L.tram ? L.tram.distanceTo(eye) : Infinity, pa: game.cityLog.slice(), steam: !!L.steam, ads: L.ads?.group.children.length ?? 0, adRedraws: L.ads?.redraws ?? 0, adFed: L.ads?.fed ?? 0, adCarry: L.ads?.carry ?? 0, adFrames: L.ads?.recent(600) ?? [], ship: L.sky.shipPos, blinkers: L.sky.blinkers, flicker: !!game.renderer.signFlicker };
    })(),
  }),
  file: () => game.file.view(),
  setLoadout: (raw) => game.file.setRaw(raw),
  toggleFile: (on) => game.file.toggle(on),
  toggleGraph: (on) => game.file.toggleGraph(on),
  buy: (id, refund) => game.file.buy(id, refund),
  sign: () => game.sign(),
  endgame: () => ({ ...game.file.endgame, wakelight: game.file.accountRecord?.wallet.wakelight ?? game.file.wakelight, rewrites: game.file.accountRecord?.rewrites ?? 0, theme: game.file.accountRecord?.theme ?? null, cosmetics: game.file.accountRecord?.cosmetics ?? [], presets: (game.file.accountRecord?.presets ?? []).map((p) => ({ name: p?.name ?? "" })), aliases: game.file.accountRecord?.aliases ?? [], audits: game.file.accountRecord?.audits ?? null, mode: game.net?.mode ?? "", gravity: game.world.gravityMult }),
  loadEndgame: () => game.file.loadEndgame(),
  claim: (id) => game.file.postEndgame("claim", { id }),
  rewrite: () => game.file.postEndgame("rewrite", {}),
  cosmetic: (body) => game.file.postEndgame("cosmetic", body),
  loadPreset: (slot) => game.file.loadPreset(slot),
  joinAudit: () => game.joinAudit(),
  mobile: () => ({
    on: game.mobile,
    engaged: !!game.touch?.engaged,
    buttons: [...document.querySelectorAll<HTMLElement>("#hud .thumbs .tc-b")].map((b) => {
      const r = b.getBoundingClientRect();
      return { id: b.dataset.b ?? "", x: r.left + r.width / 2, y: r.top + r.height / 2, w: r.width, h: r.height };
    }),
    mirror: !game.renderer.mobile,
    scale: game.renderer.post.scale,
  }),
  counter: () => {
    void game.file.ensureCounter(); // asking about the ledger is wanting it (Stage 48)
    return { view: game.file.counterState, wallet: game.file.counter?.address ?? null, last: game.file.counter?.last ?? "", info: game.file.counter?.info ?? null, tint: game.renderer.skinTint, skinMap: !!game.renderer.skinMap, skinBound: game.renderer.skinBound(), assets: { ...assetStats }, remotes: (game.net?.remoteViews() ?? []).map((r) => ({ id: r.id, name: r.name ?? "", tag: r.tag ?? "", skin: parseTag(r.tag ?? "", "").skin })) };
  },
  link: () => game.file.ensureCounter().then((c) => c?.link() ?? { ok: false, reason: "offline" }),
  buySkin: (listing) => game.file.ensureCounter().then((c) => c?.buy(listing) ?? { ok: false, reason: "offline" }),
  buySeason: () => game.file.ensureCounter().then((c) => c?.buySeason() ?? { ok: false, reason: "offline" }),
  buyRoomHours: (hours) => game.file.ensureCounter().then((c) => c?.buyRoomHours(hours) ?? { ok: false, reason: "offline" }),
  openRoom: (hours = 1, rules = {}) => game.file.ensureCounter().then((c) => c?.openRoom(hours, rules) ?? { ok: false, reason: "offline" }),
  lookupRoom: (code) => game.file.ensureCounter().then((c) => c?.lookupRoom(code) ?? { ok: false, reason: "offline" }),
  wearSkin: (token) => game.file.ensureCounter().then((c) => c?.op("wear", { token }) ?? { ok: false, reason: "offline" }),
  registerName: (name) => game.file.ensureCounter().then((c) => c?.registerName(name) ?? { ok: false, reason: "offline" }),
  reconcile: () => game.file.ensureCounter().then((c) => c?.op("reconcile") ?? { ok: false, reason: "offline" }),
  loadAsset: (id) => assetTexture(id).then((t) => !!t),
  perf: () => game.perf?.state() ?? null,
  view: () => ({ ...game.renderer.view(), calls: game.renderer.renderer.info.render.calls, breakdown: game.renderer.breakdown(), eye: { x: game.player.pos.x, y: game.player.pos.y + 1.62, z: game.player.pos.z }, spawn: game.renderer.spawnClock }),
  setView: (third) => game.renderer.setView(third),
  hideBody: (on) => {
    game.renderer.bodyHidden = on;
  },
  rig: (id) => game.renderer.rig(id),
      presentation: () => game.renderer.presentation(),
      holdFlash: (on: boolean) => game.hud.setFlashHold(on),
  kickRemote: (id) => game.renderer.kickRemote(id),
  // offline the frame loop never syncs remotes, so an injected one stays until injected away
  injectRemote: (views) => game.renderer.syncRemotes(views ?? []),
  pwa: () => pwaState(),
  crawl: () => crawl?.view() ?? null,
  // `crawl().hum` is the crawl's own request latch; this is the other half — whether a context
  // exists and whether the oscillator is running. The two came apart for the whole crawl.
  audioLive: () => ({ ready: game.audio.ready, humming: game.audio.crawlHumming }),
  screens: () => game.renderer.screens.stats(),
  crawlSkip: () => crawl?.skip() ?? false,
  crawlFinish: () => crawl?.finish(true),
  crawlPause: (on) => {
    if (crawl) crawl.paused = on;
  },
  crawlSeek: (t) => crawl?.seek(t),
  menu: () => menu?.view() ?? null,
  loading: () => loadingView(),
  menuKey: (code) => menu?.key(code),
  menuChoose: (id) => menu?.choose(id) ?? null,
  pause: () => menu?.pause(),
  menuPause: (on) => {
    if (menu) menu.paused = on;
  },
  settings: () => ({ ...game.settings, applied: { sensitivity: game.input.sensitivity, fov: game.renderer.fov, crt: game.renderer.crtLevel(), volumes: game.audio.getVolumes(), buses: game.audio.busGains() } }),
  setSetting: (key, value) => {
    const s = clampSettings({ ...game.settings, [key]: value });
    saveSettings(s);
    game.applySettings(s);
    return s;
  },
  audioCues: () => ({ ...game.audio.fired }),
  run: () => game.runView,
  renderBreakdown: () => game.renderer.breakdown(),
  payout: () => game.file.ensureCounter().then((c) => c?.op("payout") ?? { ok: false, reason: "offline" }),
  sellSkin: (token, price) => game.file.ensureCounter().then((c) => c?.sell(token, price) ?? { ok: false, reason: "offline" }),
  prizes: async () => {
    const c = await game.file.ensureCounter();
    await c?.op("prizes");
    return c?.prizes ?? [];
  },
  claimPrize: (epoch) => game.file.ensureCounter().then((c) => c?.op("claimPrize", { epoch }) ?? { ok: false, reason: "offline" }),
  hurt: (dmg) => {
    game.player.health = Math.max(1, game.player.health - dmg);
    return game.player.health;
  },
  campaign: () => game.campaign.view(),
  worldMap: (o = {}) => {
    const m = game.worldMap;
    if (!m) return null;
    if (o.open !== undefined && o.open !== m.open) m.toggle(o.open);
    if (o.select) m.select(o.select);
    const travelUrl = o.travel ? m.travel(o.travel) : null;
    return { ...m.view(), travelUrl };
  },
  figures: () => {
    const out: { id: string; x: number; z: number; yaw: number }[] = [];
    // read the scene itself, not the fields that say what was built: a figure counts if it is attached and shown
    game.renderer.scene.traverseVisible((o) => {
      if (!o.name.startsWith("fixer:")) return;
      o.updateWorldMatrix(true, false);
      const e = o.matrixWorld.elements;
      // a figure's front is its -z; the world yaw of that, in the camera's convention
      out.push({ id: o.name.slice(6), x: e[12]!, z: e[14]!, yaw: Math.atan2(e[8]!, e[10]!) });
    });
    return out;
  },
  dialogueAdvance: (choice) => game.campaign.advance(choice ?? -1),
  contracts: (on) => game.campaign.toggleContracts(on),
  launch: (id) => game.campaign.launch(id),
  crewStart: (id) => game.campaign.launchCrew(id),
  crewJoin: (code) => game.campaign.joinCrew(code),
  pickFaction: (f) => game.campaign.chooseFaction(f),
  wear: (ids) => game.campaign.wear(ids),
  playScript: (id) => game.campaign.playScript(id, () => {}),
  setMoniker: (id) => game.file.setMoniker(id),
  events: () => game.recentEvents.slice(),
  clearEvents: () => {
    game.recentEvents.length = 0;
  },
  resumeAudio: () => game.audio.resume(),
  setDrawing: (on) => {
    game.drawing = on;
  },
  connect: (cfg) => game.connect(cfg),
  reconnect: () => game.reconnect(),
  disconnect: () => game.net?.close(),
  net: () =>
    game.net
      ? {
          online: true,
          status: game.net.status,
          synced: game.synced,
          playerId: game.net.playerId,
          token: game.net.token,
          rttMs: game.net.rttMs,
          joinMs: game.net.stats.joinedAtMs ? game.net.stats.joinedAtMs - game.net.stats.connectStartMs : -1,
          joinAsks: game.net.joinAsks,
          pending: game.net.pendingInputs.length,
          stats: { ...game.net.stats },
          game: { ...game.netStats },
          remotes: game.net.remoteViews(),
          // everyone in a city room, names only (Stage 706); null in a room that does not send one
          roster: game.net.roster,
          files: game.net.files,
          kickReason: game.net.kickReason,
        }
      : null,
};

// Stage 45: PROD-only; a no-op in dev and in the headless probes.
registerServiceWorker();

// URL-driven connect: ?net=ws://host/room/name&name=ALPHA&lat=75&jitter=8&loss=0.05
{
  const q = new URLSearchParams(location.search);
  const url = q.get("net");
  if (url) {
    const lat = Number(q.get("lat") ?? 0);
    const loss = Number(q.get("loss") ?? 0);
    const jitter = Number(q.get("jitter") ?? 0);
    const seed = Number(q.get("seed") ?? 1);
    game.connect({ url, name: q.get("name") ?? "BLANK", token: q.get("token") ?? undefined, sim: lat || loss || jitter ? { latencyMs: lat, jitterMs: jitter, loss, seed } : undefined });
  }
}

/** The opening crawl plays over the booting game; headless probes skip it unless they ask for it. */
// the opening trailer plays once per browser (Stage 691), or every visit when the setting asks
const crawl = crawlWanted(bootQ, crawlSeen(), game.settings.crawlEveryTime) ? new OpeningCrawl(game.audio, Number(bootQ.get("crawlspeed") ?? 1) || 1) : null;
/**
 * The WALLET page's ledger host when the file has none (the title menu has no room and no `?shop=`):
 * the built counter-ledger, and in development nothing — the page says so instead of failing a fetch
 * to a default host that is not running, the same rule as matchmaking below.
 */
const walletFallback = COUNTER_URL ?? (HOSTS.build !== "dev" ? HOSTS.ledger : null);
/** The CRT menu flow: title cards then the menu after the crawl (or straight away); ESC in play is the pause menu. */
const menu = menuWanted(bootQ)
  ? new Menu(
      {
        audio: game.audio,
        settings: game.settings,
        applySettings: (s) => game.applySettings(s),
        openFile: () => game.file.toggle(true),
        // a phone has no pointer to lock (Stage 141): RESUME there is the menu going away
        resume: () => {
          if (!game.touch) (document.getElementById("view") as HTMLCanvasElement | null)?.requestPointerLock?.();
        },
        identityLine: () => {
          const v = game.file.identityView();
          return `${v.display} · DEPTH ${String(game.file.depth).padStart(2, "0")} · ${game.file.account}`;
        },
        books: () => ({ scrip: game.file.scrip, wakelight: game.file.wakelight, chits: game.file.chits, depth: game.file.depth, owed: game.file.counterState?.run.owed ?? 0 }),
        look: () => game.file.look,
        setLook: (code) => game.file.setLook(code),
        // one client for the WALLET page and the FILE page's Counter-Ledger: the file's (Stage 48's lazy chunk)
        wallet: {
          state: () => walletState(game.file.counter, !!(game.file.shop ?? walletFallback)),
          open: () => {
            void game.file.ensureCounter(walletFallback).then((c) => {
              if (c?.address) void c.readHoldings();
            });
          },
          act: (id) => {
            void game.file.ensureCounter(walletFallback).then((c) => c && walletAct(c, id));
          },
        },
        // PLAY's line: the campaign as the file has it (the linked ledger's save, else the local one)
        campaign: () => game.campaign.current(),
      },
      Number(bootQ.get("menuspeed") ?? 1) || 1,
    )
  : null;
if (menu) {
  // Probes step the title cards themselves. The freeze has to be set before the first card frame,
  // which is earlier than any script the harness can inject once the page has loaded.
  if (bootQ.get("menufreeze") === "1") menu.paused = true;
  game.file.onCounter = () => menu.refresh();
  // the title cards say what the trailer just said: only on a boot the trailer did not open (and not on the way back from a game)
  const fromGame = bootDesc?.kind === "menu";
  if (crawl) crawl.onFinish = () => menu.start({ cards: cardsWanted(true, fromGame) });
  else menu.start({ cards: cardsWanted(false, fromGame) });
  game.onLockLost = () => {
    // the WORLD MAP gives up the pointer to be clicked (Stage 705): that is not a pause
    if (menu.screen === "hidden" && !game.file.isOpen && !game.worldMap?.open && !crawl?.active) menu.pause();
  };
  // Stage 141: the phone's PAUSE pad is its Escape
  if (game.touch) game.touch.onPause = () => {
    if (menu.screen === "hidden" && !game.file.isOpen && !crawl?.active) menu.pause();
  };
}
game.start();

// the card walks the real stages and goes when the game is playable: the world is built (the Game
// exists), a frame drawn (one the probes' `setDrawing(false)` never draws counts as drawn), and in a
// room the Welcome in and the first exact state applied; a failed join shows the link's own line
if (bootCard) {
  const networked = bootQ.has("net");
  watchBoot(bootCard, () => ({ built: true, frames: game.drawing ? game.stats.frames : 1, networked, net: game.net ? { status: game.net.status, reason: game.net.kickReason, synced: game.synced } : null }));
}
